import { useState } from "react";
import { PencilSimple, Trash, PaperPlaneRight } from "@phosphor-icons/react";
import { fmtDateTime } from "../lib/api";
import { InitialsBadge } from "./InitialsBadge";
import { MentionText, MentionHighlight } from "./MentionText";

export function NoteItem({ n, mine, staff, onEdit, onDelete, flash, mention, onMentionDone }) {
  const openMention = mention && !mention.done;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(n.text);
  const [picked, setPicked] = useState([]);
  const save = () => {
    if (text.trim() && text.trim() !== n.text) onEdit(text.trim(), picked);
    setEditing(false);
    setPicked([]);
  };
  return (
    <div id={`note-item-${n.id}`} className={`group flex items-end gap-2.5 rounded-2xl ${mine ? "flex-row-reverse" : ""} ${flash ? "focus-glow p-2 -m-2" : ""}`} data-testid={`note-item-${n.id}`}>
      <InitialsBadge name={n.author} size={32} testId={`note-avatar-${n.id}`} />
      <div className={`max-w-[78%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
        <div className={`text-[11px] text-slate-500 mb-1 px-1 ${mine ? "text-right" : ""}`}>
          <span className="font-semibold text-slate-700">{n.author}</span> · {fmtDateTime(n.created_at)}
          {n.edited_at && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1E5FEB] font-semibold uppercase tracking-wider text-[9.5px]" title={`Edited by ${n.edited_by} · ${fmtDateTime(n.edited_at)}`} data-testid={`note-edited-tag-${n.id}`}>edited</span>}
          {mention && (
            <button onClick={() => onMentionDone(mention, !mention.done)} data-testid={`note-mention-done-${n.id}`}
              title={mention.done ? "Move back to To do" : "Mark this mention as done"}
              className={`ml-1.5 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider text-[9.5px] transition-colors ${openMention ? "bg-amber-100 text-amber-800 hover:bg-emerald-100 hover:text-emerald-700" : "bg-slate-100 text-slate-500 hover:bg-amber-100 hover:text-amber-800"}`}>
              {openMention ? "@ you · Mark done" : "@ you · Done"}
            </button>
          )}
        </div>
        <div className={`relative px-3.5 py-2.5 text-[14px] leading-relaxed break-words whitespace-pre-wrap ${
          openMention ? "bg-amber-50 border border-amber-200 " + (mine ? "rounded-2xl rounded-br-md" : "rounded-2xl rounded-bl-md") + " text-slate-900" : mine ? "bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl rounded-br-md text-slate-900" : "bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-md text-slate-900"
        } ${editing ? "w-[420px] max-w-full ring-2 ring-[#DBEAFE] border-[#1E5FEB]" : ""}`}>
          {editing ? (
            <>
              <MentionText value={text} onChange={setText} onSubmit={save} onCancel={() => { setText(n.text); setEditing(false); }} autoFocus testId={`note-edit-input-${n.id}`}
                staff={staff} onMention={(u) => setPicked((p) => [...p, u.id])} />
              <div className="mt-2 flex justify-end gap-1.5">
                <button onClick={() => { setText(n.text); setEditing(false); }} className="h-7 px-2.5 rounded-lg text-[11.5px] font-semibold text-slate-600 hover:bg-slate-100 transition-colors" data-testid={`note-edit-cancel-${n.id}`}>Cancel</button>
                <button onClick={save} className="h-7 px-3 rounded-lg bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[11.5px] font-bold transition-colors" data-testid={`note-edit-save-${n.id}`}>Save</button>
              </div>
            </>
          ) : <MentionHighlight text={n.text} mentions={n.mentions} />}
        </div>
      </div>
      {!editing && (
        <div className="flex gap-0.5 self-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button onClick={() => { setText(n.text); setEditing(true); }} title="Edit note" className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-[#EFF6FF] hover:text-[#1E5FEB] transition-colors" data-testid={`note-edit-btn-${n.id}`}>
            <PencilSimple size={14} weight="bold" />
          </button>
          <button onClick={onDelete} title="Delete note" className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors" data-testid={`note-delete-btn-${n.id}`}>
            <Trash size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

export function NoteComposer({ author, value, onChange, onSubmit, staff }) {
  const [picked, setPicked] = useState([]);
  const submit = () => { onSubmit(picked); setPicked([]); };
  return (
    <div className="flex items-end gap-2.5" data-testid="note-composer">
      <InitialsBadge name={author} size={32} testId="note-composer-avatar" />
      <div className="flex-1 rounded-2xl border border-[#E5E7EB] bg-[#FAFBFC] focus-within:bg-white focus-within:border-[#1E5FEB] focus-within:ring-2 focus-within:ring-[#DBEAFE] transition-colors px-3.5 pt-2.5 pb-2">
        <MentionText value={value} onChange={onChange} onSubmit={submit} placeholder="Write a note… type @ to mention a teammate" testId="note-input"
                     staff={staff} onMention={(u) => setPicked((p) => [...p, u.id])} />
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="text-[10.5px] text-slate-400">@ to mention · Ctrl + Enter to save</span>
          <button onClick={submit} disabled={!value.trim()} data-testid="note-add"
            className="h-8 px-3.5 rounded-lg bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[12px] font-bold inline-flex items-center gap-1.5 transition-colors">
            <PaperPlaneRight size={13} weight="fill" /> Add note
          </button>
        </div>
      </div>
    </div>
  );
}
